import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, MessageSquare, Clock } from "lucide-react";
import { PublicShell } from "../components/PublicShell";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CATEGORIES = ["General enquiry", "Support / technical help", "Recovery centre enquiry", "Feedback & suggestions", "Media & partnerships", "Privacy & data"];

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", category: "General enquiry", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact Us | TaperPath";
    let el = document.querySelector('meta[name="description"]');
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", "description"); document.head.appendChild(el); }
    el.setAttribute("content", "Get in touch with the TaperPath team — email support@taperpath.co.uk or send us a message for support, feedback or centre enquiries.");
    return () => { document.title = "TaperPath"; };
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.display_name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setSent(true);
    } catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };

  return (
    <PublicShell>
      <section className="mx-auto max-w-5xl px-6 py-12 lg:py-16">
        <div className="max-w-2xl">
          <span className="mb-4 inline-block rounded-full bg-[#8FB8DE]/25 px-4 py-1.5 text-sm font-medium text-[#2C4763]">We're here to help</span>
          <h1 className="font-head text-4xl font-extrabold leading-tight text-[#2C363F] sm:text-5xl">Contact us</h1>
          <p className="mt-4 text-lg leading-relaxed text-[#64748B]">Questions, feedback, or a recovery service you'd like listed? Send us a message and we'll get back to you.</p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.4fr]">
          <div className="space-y-4">
            <a href="mailto:support@taperpath.co.uk" data-testid="contact-email-link" className="flex items-start gap-3 rounded-2xl border border-border bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#4A7c59]/10 text-[#4A7c59]"><Mail strokeWidth={1.8} className="h-5 w-5" /></div>
              <div>
                <p className="font-head font-semibold text-[#2C363F]">Email us</p>
                <p className="mt-0.5 text-sm text-[#4A7c59]">support@taperpath.co.uk</p>
              </div>
            </a>
            <div className="flex items-start gap-3 rounded-2xl border border-border bg-white p-5 shadow-sm">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#8FB8DE]/20 text-[#2C4763]"><Clock strokeWidth={1.8} className="h-5 w-5" /></div>
              <div>
                <p className="font-head font-semibold text-[#2C363F]">Response time</p>
                <p className="mt-0.5 text-sm text-muted-foreground">We aim to reply as soon as we can.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-[#E8907E]/40 bg-[#E8907E]/5 p-5 text-sm text-[#8a4436]">
              <strong>In crisis?</strong> This form isn't monitored 24/7. If you need urgent help, call 999, or the Samaritans free on 116 123.
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-center py-10 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#4A7c59]/10 text-[#4A7c59]"><Send strokeWidth={1.8} className="h-6 w-6" /></div>
                <h2 className="mt-4 font-head text-xl font-bold text-[#2C363F]">Message sent</h2>
                <p className="mt-2 max-w-sm text-muted-foreground">Thanks for reaching out — we've received your message and will be in touch soon.</p>
                <Button data-testid="contact-send-another" onClick={() => { setSent(false); setForm({ ...form, subject: "", message: "" }); }} variant="outline" className="mt-6 rounded-full">Send another message</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm">Name</Label>
                    <Input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="mt-1 rounded-xl py-6" />
                  </div>
                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1 rounded-xl py-6" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Reason for contact</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="contact-category" className="mt-1 rounded-xl py-6"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Subject</Label>
                  <Input data-testid="contact-subject" required value={form.subject} onChange={set("subject")} placeholder="How can we help?" className="mt-1 rounded-xl py-6" />
                </div>
                <div>
                  <Label className="text-sm">Message</Label>
                  <Textarea data-testid="contact-message" required value={form.message} onChange={set("message")} placeholder="Tell us a bit more…" rows={6} className="mt-1 rounded-xl" />
                </div>
                <Button data-testid="contact-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#4A7c59] py-6 text-base font-semibold hover:bg-[#3B6347]">
                  {loading ? "Sending…" : <><Send className="mr-2 h-4 w-4" /> Send message</>}
                </Button>
                <p className="text-center text-xs text-muted-foreground">By sending this you agree we may use your email to reply. We never share it.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
